import { type ComponentProps } from 'react';
import { Lock } from 'lucide-react';

import { cn } from '@/util/utils';

/**
 * The control container from the design's Input: when a field carries a leading icon or a
 * unit (£, %), the border, background and focus glow belong to the container so the ring
 * surrounds the whole thing. Put an `InputGroupIcon` or `InputGroupAffix` beside an
 * `InputGroupInput`.
 *
 * <p>`locked` is the design's locked state (ui-control--locked): a value filled from another
 * record, shown on the muted ground in the locked text colour with a 13px padlock before it,
 * which is how the design tells "owned elsewhere" apart from "temporarily unavailable". The
 * field inside must be `readOnly`. `Input locked` does that, and also takes the field out of
 * the tab order, as the disabled fields it replaced were: a sheet's first tab stop would
 * otherwise be a value nobody can change. A click still focuses it, to select and copy.</p>
 */
function InputGroup({
    className,
    locked = false,
    children,
    ref,
    ...props
}: ComponentProps<'div'> & { locked?: boolean }) {
    return (
        <div
            data-slot="input-group"
            data-locked={locked || undefined}
            ref={ref}
            className={cn(
                'bg-card border-input text-foreground flex h-9 w-full min-w-0 items-center gap-2 rounded-md border px-3 shadow-xs pointer-coarse:min-h-11',
                'transition-[color,box-shadow,border-color] duration-(--duration-fast)',
                'focus-within:border-ring focus-within:shadow-[0_0_0_3px_var(--outline)]',
                'has-aria-invalid:border-destructive has-aria-invalid:focus-within:shadow-[0_0_0_3px_var(--outline-destructive)]',
                'has-disabled:bg-muted has-disabled:text-text-locked has-disabled:shadow-none',
                locked && 'bg-muted text-text-locked',
                className,
            )}
            {...props}
        >
            {locked ? (
                <Lock
                    data-slot="input-group-lock"
                    aria-hidden
                    className="text-text-locked size-[13px] shrink-0"
                />
            ) : null}
            {children}
        </div>
    );
}

/** The bare field inside a group: no border of its own, the group draws it. */
function InputGroupInput({ className, ref, type = 'text', ...props }: ComponentProps<'input'>) {
    return (
        <input
            data-slot="input-group-input"
            ref={ref}
            type={type}
            className={cn(
                'placeholder:text-muted-foreground h-full w-full min-w-0 flex-1 bg-transparent text-sm outline-none disabled:cursor-not-allowed pointer-coarse:text-base',
                '[&::-webkit-search-cancel-button]:appearance-none',
                className,
            )}
            {...props}
        />
    );
}

/** A leading Lucide icon, muted, at the 15px ui-input draws its icon (form.js). */
function InputGroupIcon({ className, ref, ...props }: ComponentProps<'span'>) {
    return (
        <span
            data-slot="input-group-icon"
            aria-hidden
            ref={ref}
            className={cn(
                'text-muted-foreground flex shrink-0 items-center [&_svg]:size-[15px]',
                className,
            )}
            {...props}
        />
    );
}

/** A unit inside the box: £ before, % after. */
function InputGroupAffix({ className, ref, ...props }: ComponentProps<'span'>) {
    return (
        <span
            data-slot="input-group-affix"
            ref={ref}
            className={cn('text-muted-foreground shrink-0 text-sm', className)}
            {...props}
        />
    );
}

export { InputGroup, InputGroupInput, InputGroupIcon, InputGroupAffix };
